import { useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import Navigation from "@/components/Navigation"
import Footer from "@/components/Footer"
import NotFound from "./NotFound"
import { Button } from "@/components/ui/button"
import { Check, Users, BookOpen, Truck } from "lucide-react"

type Occasion = {
  slug: string
  label: string
  h1: string
  metaTitle: string
  metaDescription: string
  shareImage: string
  intro: string
  images: string[]
  link: string
  benefits: { title: string; text: string }[]
  ideas: string[]
  faq: { q: string; a: string }[]
}

export const OCCASION_PAGES: Occasion[] = [
  {
    slug: "album-photo-voyage",
    label: "Album de Voyage",
    h1: "Album photo de voyage : racontez vos aventures à plusieurs",
    metaTitle: "Album photo de voyage personnalisé | Wememberz",
    metaDescription: "Créez un album photo de voyage collaboratif : réunissez les photos de tous vos compagnons de route et imprimez un livre souvenir unique.",
    shareImage: "/images/social/album-photo-voyage.jpg",
    intro: "Road trip, tour du monde, week-end entre amis ou vacances en famille : chacun rentre avec ses propres photos. Avec Wememberz, tout le monde les partage au même endroit et vous créez ensemble un album photo de voyage qui raconte vraiment votre aventure.",
    images: ["/images/occasions/voyage1.jpg", "/images/occasions/voyage2.jpg", "/images/occasions/voyage3.jpg"],
    link: "/create_book?open=2",
    benefits: [
      { title: "Toutes les photos réunies", text: "Invitez vos compagnons de voyage avec un simple code : leurs photos arrivent directement dans l'album." },
      { title: "Un carnet de route", text: "Organisez vos pages par étape, par ville ou par journée pour revivre l'itinéraire." },
      { title: "Impression soignée", text: "Papier de qualité et couverture personnalisée, livré chez vous." },
    ],
    ideas: ["Carnet de road trip", "Voyage de noces", "Tour du monde", "Week-end entre amis", "Voyage scolaire", "Vacances en famille"],
    faq: [
      { q: "Combien de personnes peuvent ajouter des photos ?", a: "Autant que vous le souhaitez : partagez le code de l'album et chaque participant peut ajouter ses photos." },
      { q: "Puis-je ajouter du texte et des légendes ?", a: "Oui, vous pouvez légender vos photos et ajouter des textes pour raconter chaque étape du voyage." },
    ],
  },
  {
    slug: "album-photo-mariage",
    label: "Album de Mariage",
    h1: "Album photo de mariage : les souvenirs de tous vos invités",
    metaTitle: "Album photo de mariage collaboratif | Wememberz",
    metaDescription: "Réunissez les photos du photographe et de vos invités dans un album de mariage personnalisé, imprimé avec soin et livré chez vous.",
    shareImage: "/images/social/album-photo-mariage.jpg",
    intro: "Le photographe capture les grands moments, mais vos invités saisissent les instants spontanés. Wememberz rassemble toutes ces photos dans un seul album de mariage, pour ne rien oublier de votre plus beau jour.",
    images: ["/images/occasions/mariage1.jpg", "/images/occasions/mariage2.jpg", "/images/occasions/mariage3.jpg"],
    link: "/create_book?open=5",
    benefits: [
      { title: "Les photos des invités", text: "Partagez un code le jour J : chacun ajoute ses clichés depuis son téléphone." },
      { title: "Un livre d'or en images", text: "Ajoutez les mots doux de vos proches à côté de leurs photos." },
      { title: "Un cadeau durable", text: "Un bel album imprimé à offrir aux parents et témoins." },
    ],
    ideas: ["Album du jour J", "Livre d'or photo", "Enterrement de vie de célibataire", "Fiançailles", "Anniversaire de mariage", "Cadeau pour les témoins"],
    faq: [
      { q: "Les invités doivent-ils créer un compte ?", a: "Ils rejoignent l'album grâce au code partagé et peuvent y déposer leurs photos en quelques secondes." },
      { q: "Puis-je commander plusieurs exemplaires ?", a: "Oui, vous pouvez commander autant d'exemplaires que nécessaire, pour vous et vos proches." },
    ],
  },
  {
    slug: "album-photo-famille",
    label: "Album de Famille",
    h1: "Album photo de famille : rassemblez les souvenirs de chacun",
    metaTitle: "Album photo de famille personnalisé | Wememberz",
    metaDescription: "Créez un album photo de famille à plusieurs : grands-parents, parents et enfants partagent leurs photos pour un livre souvenir unique.",
    shareImage: "/images/social/album-photo-famille.jpg",
    intro: "Les photos de famille sont éparpillées dans les téléphones de chacun. Wememberz permet à toute la famille de les réunir dans un album commun, idéal pour les fêtes, les anniversaires ou simplement pour garder une trace de l'année.",
    images: ["/images/occasions/famille1.jpg", "/images/occasions/famille2.jpg", "/images/occasions/famille3.jpg"],
    link: "/create_book?open=3",
    benefits: [
      { title: "Toute la famille participe", text: "Oncles, cousins, grands-parents : chacun ajoute ses photos où qu'il soit." },
      { title: "L'album de l'année", text: "Retracez une année de moments partagés, des fêtes aux vacances." },
      { title: "Le cadeau parfait", text: "Offrez un album aux grands-parents pour Noël ou la fête des mères." },
    ],
    ideas: ["Album de l'année", "Cadeau fête des mères", "Réunion de famille", "Anniversaire des grands-parents", "Noël en famille", "Arbre généalogique en photos"],
    faq: [
      { q: "Les grands-parents peuvent-ils participer ?", a: "Oui, l'ajout de photos est simple et accessible, même pour les moins habitués au numérique." },
      { q: "Quels formats sont disponibles ?", a: "Plusieurs formats sont proposés, du carré au paysage, pour s'adapter à vos photos." },
    ],
  },
  {
    slug: "album-photo-naissance",
    label: "Album de Naissance",
    h1: "Album photo de naissance : les premiers moments de bébé",
    metaTitle: "Album photo de naissance personnalisé | Wememberz",
    metaDescription: "Créez l'album de naissance de votre bébé avec les photos de toute la famille : premiers jours, premiers sourires, premiers pas.",
    shareImage: "/images/social/album-photo-naissance.jpg",
    intro: "Premiers jours, premiers sourires, premiers pas : chaque étape mérite d'être gardée précieusement. Avec Wememberz, parents, grands-parents et proches partagent leurs photos pour créer un album de naissance plein de tendresse.",
    images: ["/images/occasions/naissance1.jpg", "/images/occasions/naissance2.jpg", "/images/occasions/naissance3.jpg"],
    link: "/create_book?open=2",
    benefits: [
      { title: "Les photos de tous les proches", text: "Chaque visite à la maternité devient un souvenir partagé dans l'album." },
      { title: "Mois après mois", text: "Suivez la croissance de bébé de sa naissance à son premier anniversaire." },
      { title: "Un souvenir pour la vie", text: "Un livre imprimé qu'il pourra feuilleter en grandissant." },
    ],
    ideas: ["Première année de bébé", "Grossesse et naissance", "Baptême", "Premier anniversaire", "Cadeau de naissance", "Album pour les grands-parents"],
    faq: [
      { q: "Est-ce un bon cadeau de naissance ?", a: "Oui : offrez l'album et invitez les proches à y ajouter leurs photos, c'est un cadeau collectif très apprécié." },
      { q: "Mes photos restent-elles privées ?", a: "Seules les personnes à qui vous donnez le code peuvent accéder à l'album." },
    ],
  },
]

const SITE = "https://www.wememberz.com"

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement("meta")
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

export default function OccasionPage({ slug: forced }: { slug?: string }) {
  const params = useParams()
  const slug = forced || params.slug
  const o = OCCASION_PAGES.find((p) => p.slug === slug)

  useEffect(() => {
    if (!o) return
    const prevTitle = document.title
    document.title = o.metaTitle
    setMeta("name", "description", o.metaDescription)
    setMeta("property", "og:title", o.metaTitle)
    setMeta("property", "og:description", o.metaDescription)
    setMeta("property", "og:url", `${SITE}/${o.slug}`)
    setMeta("property", "og:image", `${SITE}${o.shareImage}`)
    setMeta("name", "twitter:image", `${SITE}${o.shareImage}`)
    let canon = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!canon) {
      canon = document.createElement("link")
      canon.rel = "canonical"
      document.head.appendChild(canon)
    }
    canon.href = `${SITE}/${o.slug}`
    const ld = document.createElement("script")
    ld.type = "application/ld+json"
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: o.label, item: `${SITE}/${o.slug}` },
      ],
    })
    document.head.appendChild(ld)
    window.scrollTo(0, 0)
    return () => {
      document.title = prevTitle
      ld.remove()
    }
  }, [o])

  if (!o) return <NotFound />
  const others = OCCASION_PAGES.filter((p) => p.slug !== o.slug)
  const icons = [Users, BookOpen, Truck]

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-32 md:pt-36">
        <nav aria-label="Fil d'Ariane" className="container mx-auto px-4 lg:px-8 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary">Accueil</Link> <span className="mx-1">/</span>
          <span className="text-foreground">{o.label}</span>
        </nav>

        <section className="container mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-10 items-center mb-20">
          <div className="space-y-6">
            <h1 className="text-3xl md:text-5xl font-display font-bold text-foreground leading-tight">{o.h1}</h1>
            <p className="text-lg text-muted-foreground">{o.intro}</p>
            <Button variant="rainbow" size="lg" asChild>
              <a href={o.link}>Créer mon {o.label.toLowerCase()}</a>
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={o.images[0]} alt={`${o.label} - exemple 1`} className="col-span-2 w-full h-64 object-cover rounded-2xl shadow-card" />
            <img src={o.images[1]} alt={`${o.label} - exemple 2`} loading="lazy" className="w-full h-40 object-cover rounded-2xl shadow-card" />
            <img src={o.images[2]} alt={`${o.label} - exemple 3`} loading="lazy" className="w-full h-40 object-cover rounded-2xl shadow-card" />
          </div>
        </section>

        <section className="bg-soft-cream/40 py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <h2 className="text-2xl md:text-4xl font-display font-bold text-center mb-10">
              Pourquoi choisir Wememberz pour votre <span className="text-gradient">{o.label.toLowerCase()}</span> ?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {o.benefits.map((b, i) => {
                const Icon = icons[i]
                return (
                  <div key={b.title} className="bg-card rounded-2xl p-6 shadow-card">
                    <Icon className="w-8 h-8 text-primary mb-3" />
                    <h3 className="text-lg font-bold mb-2">{b.title}</h3>
                    <p className="text-muted-foreground text-sm">{b.text}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        <section className="container mx-auto px-4 lg:px-8 py-16">
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-8 text-center">Comment ça marche ?</h2>
          <ol className="grid md:grid-cols-3 gap-6">
            {["Créez votre album et choisissez son format", "Invitez vos proches avec le code de l'album pour qu'ils ajoutent leurs photos", "Mettez en page, commandez, et recevez votre livre chez vous"].map((s, i) => (
              <li key={s} className="flex gap-4 items-start">
                <span className="shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center">{i + 1}</span>
                <p className="pt-2">{s}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="container mx-auto px-4 lg:px-8 pb-16">
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-6">Idées d'albums</h2>
          <ul className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {o.ideas.map((idea) => (
              <li key={idea} className="flex items-center gap-2"><Check className="w-5 h-5 text-primary" />{idea}</li>
            ))}
          </ul>
        </section>

        <section className="container mx-auto px-4 lg:px-8 pb-16 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-6">Questions fréquentes</h2>
          <div className="space-y-4">
            {o.faq.map((f) => (
              <details key={f.q} className="bg-card rounded-xl p-5 shadow-card">
                <summary className="font-semibold cursor-pointer">{f.q}</summary>
                <p className="mt-3 text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button variant="rainbow" size="lg" asChild>
              <a href={o.link}>Commencer mon album</a>
            </Button>
          </div>
        </section>

        <section className="bg-soft-cream/40 py-12">
          <div className="container mx-auto px-4 lg:px-8">
            <h2 className="text-xl md:text-2xl font-display font-bold mb-6 text-center">Découvrez aussi</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {others.map((p) => (
                <Link key={p.slug} to={`/${p.slug}`} className="group bg-card rounded-2xl overflow-hidden shadow-card">
                  <img src={p.images[0]} alt={p.label} loading="lazy" className="w-full h-32 object-cover group-hover:scale-105 transition-transform" />
                  <p className="p-4 font-semibold text-center">{p.label}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}